import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product SKU.
 * This component listens for variant update events and updates the SKU display accordingly.
 * It handles SKU updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} skuContainer - The container element for the SKU
 * @property {HTMLElement} sku - The span element that displays the SKU text
 *
 * @extends {Component<Refs>}
 */
class ProductSkuComponent extends Component {
  requiredRefs = ['skuContainer', 'sku', 'barcodeContainer', 'barcodeVisual'];

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(ThemeEvents.variantUpdate, this.updateSku);

    // Initial render
    this.renderBarcode();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(ThemeEvents.variantUpdate, this.updateSku);
  }

  /**
   * Updates the SKU and Barcode.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateSku = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Use the variant data from the event
    if (event.detail.resource) {
      const variantSku = event.detail.resource.sku || '';
      const variantBarcode = event.detail.resource.barcode || '';

      // Update SKU container
      if (this.refs.skuContainer instanceof HTMLElement) {
        this.refs.skuContainer.style.display = variantSku ? 'block' : 'none';
      }
      if (this.refs.sku instanceof HTMLElement) {
        this.refs.sku.textContent = variantSku;
      }

      // Update Barcode container
      if (this.refs.barcodeContainer instanceof HTMLElement) {
        this.refs.barcodeContainer.style.display = variantBarcode ? 'block' : 'none';
      }

      // Show the entire component if either exists
      this.style.display = (variantSku || variantBarcode) ? 'block' : 'none';

      // Render visual barcode
      this.renderBarcode(variantBarcode);
    }
  };

  /**
   * Renders the visual barcode using JsBarcode library.
   * @param {string} [barcodeValue] - The barcode value to render.
   */
  renderBarcode(barcodeValue) {
    const value = barcodeValue || this.refs.barcodeContainer?.dataset?.barcode;
    const svgElement = this.refs.barcodeVisual;
    if (!value || !svgElement) return;

    if (typeof window.JsBarcode === 'function') {
      try {
        // Clear previous content to avoid overlapping
        while (svgElement.firstChild) {
          svgElement.removeChild(svgElement.firstChild);
        }

        // Detect specific formats for the "packaging" look
        let format = "CODE128";
        if (/^\d{12}$/.test(value)) format = "UPC";
        if (/^\d{13}$/.test(value)) format = "EAN13";

        window.JsBarcode(svgElement, value, {
          format: format,
          lineColor: "#000",
          width: 2.2,
          height: 70,
          displayValue: true,
          fontSize: 20,
          textMargin: 6,
          background: "transparent"
        });
      } catch (e) {
        // Fallback to CODE128 if auto fails
        try {
          window.JsBarcode(svgElement, value, {
            format: "CODE128",
            height: 40,
            displayValue: true
          });
        } catch (e2) {
          console.error("JsBarcode failed completely", e2);
        }
      }
    } else {
      // Library might still be loading
      setTimeout(() => this.renderBarcode(value), 500);
    }
  }
}

if (!customElements.get('product-sku-component')) {
  customElements.define('product-sku-component', ProductSkuComponent);
}
